import { midwayFetch } from './auth/midway';
import {
    ChatArtifact,
    ChatResponse,
    Citation,
    ToolActivity,
} from './chat-types';

type ApiErrorBody = {
    error?: string;
};

async function readError(response: Response): Promise<string> {
    try {
        const body = (await response.json()) as ApiErrorBody;
        return body.error ?? `Request failed with status ${response.status}`;
    } catch {
        return `Request failed with status ${response.status}`;
    }
}

async function authorizedFetch(path: string, init: RequestInit = {}) {
    return midwayFetch(path, init);
}

export default async function apiRequest<T>(
    path: string,
    init: RequestInit = {},
): Promise<T> {
    const response = await authorizedFetch(path, init);
    if (!response.ok) {
        throw new Error(await readError(response));
    }
    return (await response.json()) as T;
}

type StreamHandlers = {
    onStatus?: (message: string, toolActivity?: ToolActivity) => void;
    onTextDelta?: (delta: string) => void;
    onArtifact?: (artifact: ChatArtifact) => void;
    onCitation?: (citation: Citation) => void;
};

type StreamEvent = {
    name: string;
    data: unknown;
};

function parseJson(value: string): unknown {
    const payload = value.trim();
    if (!payload || payload === '[DONE]') {
        return undefined;
    }
    try {
        return JSON.parse(payload) as unknown;
    } catch {
        return payload;
    }
}

function parseEvent(frame: string): StreamEvent | undefined {
    let name = 'message';
    const data: string[] = [];
    frame.replace(/\r/g, '').split('\n').forEach((line) => {
        if (line.startsWith('event:')) {
            name = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
            data.push(line.slice(5).trimStart());
        }
    });
    if (data.length === 0) {
        return undefined;
    }
    return { name, data: parseJson(data.join('\n')) };
}

function asObject(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

export async function streamChatRequest(
    path: string,
    init: RequestInit,
    handlers: StreamHandlers | ((text: string) => void),
): Promise<ChatResponse> {
    const callbacks: StreamHandlers =
        typeof handlers === 'function' ? { onTextDelta: handlers } : handlers;
    const response = await authorizedFetch(path, init);
    if (!response.ok) {
        throw new Error(await readError(response));
    }

    const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
    if (contentType.includes('application/json')) {
        return (await response.json()) as ChatResponse;
    }

    if (!response.body) {
        return { message: await response.text() };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let completeText = '';
    let pending = '';
    let completed = false;
    const artifacts: ChatArtifact[] = [];
    const citations: Citation[] = [];
    let tools: ToolActivity[] = [];
    let runtimeSessionId: string | undefined;
    const emitDelta = (delta: string) => {
        if (delta) {
            completeText += delta;
            callbacks.onTextDelta?.(delta);
        }
    };

    const consumeEvent = (frame: string) => {
        const event = parseEvent(frame);
        if (!event) {
            return;
        }
        const data = asObject(event.data);
        if (event.name === 'status') {
            const message = typeof data.message === 'string' ? data.message : 'Working';
            callbacks.onStatus?.(
                message,
                data.toolActivity as ToolActivity | undefined,
            );
        } else if (event.name === 'text_delta' || event.name === 'message') {
            let delta = '';
            if (typeof data.delta === 'string') {
                delta = data.delta;
            } else if (typeof event.data === 'string') {
                delta = event.data;
            }
            emitDelta(delta);
        } else if (event.name === 'artifact') {
            const artifact = data.artifact as ChatArtifact | undefined;
            if (
                artifact &&
                (('artifactType' in artifact && Boolean(artifact.artifactType)) ||
                    ('type' in artifact && (artifact.type === 'table' || artifact.type === 'chart')))
            ) {
                artifacts.push(artifact);
                callbacks.onArtifact?.(artifact);
            }
        } else if (event.name === 'citation') {
            const citation = data.citation as Citation | undefined;
            if (citation?.label) {
                citations.push(citation);
                callbacks.onCitation?.(citation);
            }
        } else if (event.name === 'complete') {
            completed = true;
            tools = Array.isArray(data.tools) ? (data.tools as ToolActivity[]) : tools;
            runtimeSessionId =
                typeof data.runtimeSessionId === 'string'
                    ? data.runtimeSessionId
                    : undefined;
        } else if (event.name === 'error') {
            throw new Error(
                typeof data.message === 'string'
                    ? data.message
                    : 'The agent could not complete the request.',
            );
        }
    };

    const readNextChunk = async (): Promise<void> => {
        const result = await reader.read();
        pending = `${pending}${decoder.decode(result.value, {
            stream: !result.done,
        })}`
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n');

        if (contentType.includes('text/event-stream')) {
            const events = pending.split('\n\n');
            pending = events.pop() ?? '';
            events.some((event) => {
                consumeEvent(event);
                return completed;
            });
        } else if (pending) {
            emitDelta(pending);
            pending = '';
        }
        if (!result.done && !completed) {
            return readNextChunk();
        }
        return undefined;
    };

    try {
        await readNextChunk();
        if (pending && contentType.includes('text/event-stream') && !completed) {
            consumeEvent(pending);
        }
        if (contentType.includes('text/event-stream') && !completed) {
            throw new Error('The agent response was interrupted. Please try again.');
        }

        return {
            message: completeText,
            artifacts,
            citations,
            tools,
            runtimeSessionId,
        };
    } finally {
        await reader.cancel().catch(() => undefined);
        try {
            reader.releaseLock();
        } catch {
            // The stream may have released the reader while handling cancellation.
        }
    }
}
